import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import useMediaQuery from "@/hooks/useMediaQuery";
import classNames from "classnames";
import { isFunction, isNumber, isObjectLike, isString } from "lodash";
import { isEmptyAry, isNotSet } from "@/tools/format.checker";
import { v4 } from "uuid";
import {
  SortingAscIcon,
  SortingDescIcon,
  SortingNormalIcon,
} from "@/assets/icon";
import ConditionalFragment from "@/components/General/ConditionalFragment";

const sortingValueHandler = <Data extends Record<string, any>>(
  data: Data,
  accessor: keyof Data,
  column: Column<Data>
) => {
  let value: any = data[accessor];
  const { formatter, sortingValueFormatter } = column;
  if (sortingValueFormatter) value = sortingValueFormatter(data);
  else if (formatter) {
    const fValue = formatter(data);
    if (!isFunction(fValue)) value = fValue;
  }

  return value;
};

export interface Column<Data> {
  readonly id?: string;
  header: string;
  accessor: keyof Data;
  flex?: number;
  formatter?: (data: Data) => string | number | React.ReactElement;
  hidden?: boolean | ((data: Data) => boolean);
  sortable?: boolean;
  sortingValueFormatter?: (data: Data) => string | null | number | undefined;
}

interface TableComponent extends CustomizeFunctionComponent {
  <Data extends Record<string, any>>(
    props: PropsWithChildren<{
      data: Array<Data>;
      columns: Array<Column<Data>>;
      lastRowClassName?: string;
      showEmpty?: boolean;
      stickyOffsetClassName?: string;
    }>
  ): ReturnType<React.FC>;
}

interface SortingIconComponent extends CustomizeFunctionComponent {
  <Data extends Record<string, any>>(
    props: PropsWithChildren<{
      sortable?: boolean;
      accessor: keyof Data;
      sortingColumn: {
        accessor: keyof Data;
        asc: boolean;
      } | null;
    }>
  ): ReturnType<React.FC>;
}

const SortingIcon: SortingIconComponent = ({
  sortable,
  accessor,
  sortingColumn,
}) => {
  if (sortable) {
    if (sortingColumn?.accessor === accessor) {
      if (sortingColumn.asc) {
        return <SortingAscIcon className={"ml-1 mt-0.5"} />;
      } else {
        return <SortingDescIcon className={"ml-1 mt-0.5"} />;
      }
    } else {
      return <SortingNormalIcon className={"ml-1 mt-0.5"} />;
    }
  } else {
    return null;
  }
};

const Table: TableComponent = ({
  data,
  columns,
  lastRowClassName,
  showEmpty = false,
  stickyOffsetClassName,
}) => {
  const [sortingColumn, setSortingColumn] = useState<{
    accessor: keyof typeof data[number];
    asc: boolean;
  } | null>(null);

  const match = useMediaQuery("(min-width: 472px)");

  const onSortableColumnClick = useCallback(
    (accessor: keyof typeof data[number]) => {
      setSortingColumn((prev) => {
        if (!prev || prev.accessor !== accessor) return { accessor, asc: true };
        if (prev.accessor === accessor) {
          if (prev.asc) return { accessor, asc: false };
          else return null;
        } else return null;
      });
    },
    []
  );

  const serialized = useMemo(
    () => data.map((d) => ({ ...d, id: v4() })),
    [data]
  );

  const handledData = useMemo(() => {
    if (!sortingColumn) return [...serialized];
    else
      return [...serialized].sort((a, b) => {
        const { accessor, asc } = sortingColumn;
        const column = columns.find((column) => column.accessor === accessor)!;

        let aValue: any = sortingValueHandler(a, accessor, column);
        let bValue: any = sortingValueHandler(b, accessor, column);

        if (isObjectLike(aValue)) aValue = JSON.stringify(aValue);
        if (isObjectLike(bValue)) bValue = JSON.stringify(aValue);
        if (asc) {
          if (isNumber(aValue) || !isNaN(Number(aValue)))
            return Number(aValue) > Number(bValue ?? 0) ? 1 : -1;
          if (isNotSet(aValue)) return isNotSet(bValue) ? 1 : -1;
          if (isString(aValue))
            return aValue.localeCompare(
              (bValue as undefined | null | string) ?? ""
            );
          return 0;
        } else {
          if (isNumber(bValue) || !isNaN(Number(bValue)))
            return Number(aValue ?? 0) > Number(bValue) ? -1 : 1;
          if (isNotSet(bValue)) return isNotSet(aValue) ? -1 : 1;
          if (isString(bValue))
            return bValue.localeCompare(
              (aValue as undefined | null | string) ?? ""
            );
          return 0;
        }
      });
  }, [serialized, sortingColumn]);

  return (
    <div className={"relative text-grey-2"}>
      <div
        className={classNames(
          "md:sticky",
          stickyOffsetClassName ?? "md:-top-12 lg:top-[-4.5rem]",
          "hidden md:flex",
          "font-semibold text-grey-1",
          "border-b border-solid border-grey-4",
          "overflow-hidden"
        )}
      >
        {columns.map(({ header, flex, sortable, accessor }) => {
          return (
            <p
              onClick={
                sortable ? () => onSortableColumnClick(accessor) : undefined
              }
              key={header}
              style={{
                flex: flex ?? 1,
              }}
              className={classNames(
                "flex items-center first:justify-start justify-end",
                "first:pl-10 last:pr-10 py-5",
                "bg-white",
                {
                  "cursor-pointer": sortable,
                }
              )}
            >
              {header}
              <SortingIcon
                sortingColumn={sortingColumn}
                sortable={sortable}
                accessor={accessor}
              />
            </p>
          );
        })}
      </div>
      <div className={"divide-y divide-grey-4"}>
        <ConditionalFragment condition={isEmptyAry(handledData) && showEmpty}>
          <p className={"py-5 text-grey-3 text-center"}>無資料顯示</p>
        </ConditionalFragment>
        {handledData.map((data, rowIndex, rows) => {
          return (
            <div
              key={data.id}
              className={classNames(
                "flex flex-col md:flex-row",
                "md:px-10",
                "md:text-right",
                "divide-y divide-grey-4 md:divide-none",
                rows.length - 1 === rowIndex && lastRowClassName
              )}
            >
              {columns.map((rowData, index) => {
                const { accessor, formatter, flex = 1, header } = rowData;
                const value = formatter?.(data) ?? data[accessor];
                const hidden =
                  typeof rowData.hidden === "function"
                    ? rowData.hidden(data)
                    : rowData.hidden ?? false;

                if (hidden)
                  return (
                    <p
                      key={`${accessor}`}
                      style={{ flex }}
                      className={
                        "hidden md:flex px-5 md:px-0 flex-1 py-5 flex-shrink-0 truncate"
                      }
                    />
                  );

                if (index === 0) {
                  return (
                    <p
                      key={`${accessor}`}
                      style={{ flex: match ? flex : undefined }}
                      className={classNames(
                        "flex px-5 md:px-0 text-left py-5 bg-grey-4 md:bg-transparent flex-shrink-0 truncate"
                      )}
                    >
                      <span className={"flex-1 md:hidden font-bold"}>
                        {header}
                      </span>
                      <span className={"flex-1 truncate"} title={`${value}`}>
                        {value}
                      </span>
                    </p>
                  );
                } else {
                  return (
                    <p
                      key={`${accessor}`}
                      style={{ flex }}
                      className={
                        "flex px-5 md:px-0 flex-1 py-5 flex-shrink-0 truncate"
                      }
                    >
                      <span className={"flex-1 md:hidden font-bold"}>
                        {header}
                      </span>
                      <span className={"flex-1 truncate"} title={`${value}`}>
                        {value}
                      </span>
                    </p>
                  );
                }
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Table;
